import MeetingRoomIcon from "@mui/icons-material/MeetingRoom";
import {
	Alert,
	AlertColor,
	Box,
	Button,
	Container,
	IconButton,
	Snackbar,
	Tooltip,
} from "@mui/material";
import {
	MRT_ColumnDef,
	MRT_Table,
	useMaterialReactTable,
} from "material-react-table";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useKeycloak } from "react-keycloak-manager";
import { UserLevel } from "../interfaces/Enums";
import { EnumUtils, GetMRTLocalization } from "react-vevy-library";
import {
	getGatesEnabled,
	openDoor,
	refreshGates,
} from "../helpers/RestManager";
import { ExtendedProfile, IGate } from "../interfaces/Entities";

export default function GatesPage() {
	const { authenticatedUser, isAuthenticated } = useKeycloak();
	const keyCloak = useKeycloak();
	const { t, i18n } = useTranslation();

	const [doors, setDoors] = useState<IGate[]>([]);
	const [openSnack, setOpenSnack] = useState(false);
	const [snackMessage, setSnackMessage] = useState("");
	const [severity, setSeverity] = useState<AlertColor>("success");
	const [refreshDevices, setRefreshDevices] = useState(0);
	const [isAdmin, setIsAdmin] = useState<boolean>(false);
	const [isLoading, setIsLoading] = useState(false);

	const fetchGates = useCallback(async () => {
		if (isAuthenticated) {
			setIsLoading(true);
			var gates = await getGatesEnabled(keyCloak);
			setDoors(gates ?? []);
			setIsLoading(false);
		} else setDoors([]);
	}, [isAuthenticated, refreshDevices]);

	const openGate = async (zone: string, name: string, description: string) => {
		var result = await openDoor(keyCloak, {
			zone: zone,
			name: name,
		});
		setOpenSnack(true);
		if (result == "OK") {
			setSnackMessage(`${description} ${t("gates.opened")}`);
			setSeverity("success");
		} else {
			setSnackMessage(`${description} ${result}`);
			setSeverity("error");
		}
		console.log(result);
	};

	const handleClose = () => {
		setOpenSnack(false);
	};

	const refreshDevice = async () => {
		await refreshGates(keyCloak);
		setRefreshDevices(Date.now());
	};

	useEffect(() => {
		var extUser = authenticatedUser?.profile as ExtendedProfile;
		var userLevl = extUser?.VppUserLevel.toLowerCase();
		var adminLevel = EnumUtils.enumValueToText(
			UserLevel.Admin,
			UserLevel,
		).toLowerCase();
		var superUserLevel = EnumUtils.enumValueToText(
			UserLevel.SuperUser,
			UserLevel,
		).toLowerCase();
		setIsAdmin(userLevl === adminLevel || userLevl === superUserLevel);
	}, [isAuthenticated]);

	useEffect(() => {
		fetchGates();
	}, [fetchGates]);

	const columns = useMemo<MRT_ColumnDef<IGate>[]>(
		() => [
			...(isAdmin
				? [
						{
							accessorKey: "zone" as const,
							header: t("gates.zone"),
							size: 200,
						},
						{
							accessorKey: "name" as const,
							header: t("gates.name"),
							size: 400,
						},
					]
				: []),
			{
				accessorKey: "friendlyName",
				header: t("gates.friendlyName"),
				size: 300,
			},
		],
		[i18n.language, t, isAdmin],
	);

	const table = useMaterialReactTable({
		data: doors,
		initialState: {
			density: "compact",
		},
		columns,
		enableTableHead: true,
		enableStickyHeader: true,
		enableColumnResizing: true,
		columnResizeMode: "onChange",
		enableColumnOrdering: false,
		enableColumnActions: false,
		enableColumnFilters: false,
		enablePagination: false,
		enableRowActions: true,
		positionActionsColumn: "first",
		localization: GetMRTLocalization(),
		enableMultiSort: false,
		paginationDisplayMode: "default",
		state: {
			isLoading: isLoading,
		},
		displayColumnDefOptions: {
			"mrt-row-actions": {
				header: t("gates.actions"),
				size: 100, // ~10%
			},
		},

		renderRowActions: ({ row }) => (
			<Box sx={{ display: "flex", gap: 1 }}>
				<Tooltip title={t("gates.openDoor")}>
					<IconButton
						size='small'
						onClick={() => {
							openGate(
								row.original.zone,
								row.original.name,
								row.original.friendlyName,
							);
						}}
					>
						<MeetingRoomIcon />
					</IconButton>
				</Tooltip>
			</Box>
		),
	});

	return (
		<Container
			sx={{
				p: 2,
				flex: 1,
				minHeight: 0,
				display: "flex",
				flexDirection: "column",
				justifyContent: "space-between",
			}}
		>
			<MRT_Table table={table} />
			<Snackbar
				open={openSnack}
				autoHideDuration={3000}
				onClose={handleClose}
				message={snackMessage}
			>
				<Alert onClose={handleClose} severity={severity} sx={{ width: "100%" }}>
					{snackMessage}
				</Alert>
			</Snackbar>
			{isAdmin && (
				<Box sx={{ display: "flex", justifyContent: "flex-end", pt: 3 }}>
					<Tooltip title={t("gates.refreshText")}>
						<Button variant='contained' onClick={refreshDevice}>
							{t("gates.refresh")}
						</Button>
					</Tooltip>
				</Box>
			)}
		</Container>
	);
}
